import clsx from "clsx";
import React from "react";
import { Box, DialogProps, TextField, Button } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { BaseDialog } from "./BaseDialog";
import LoadingActionButton from "../button/LoadingActionButton";
import { useCurrentBrand } from "../../hooks/useCurrentWorkspace";

const useStyles = makeStyles((theme) => ({
  root: {},

  paper: {
    maxWidth: 520,
    width: 520,
  },

  field: {
    marginBottom: theme.spacing(2),
  },

  messageInput: {
    cursor: "text",
    height: 180,

    "& > textarea": {
      height: "100% !important",
    },
  },

  buttons: {
    display: "flex",
    marginTop: theme.spacing(3),
  },

  cancelButton: {
    fontSize: 16,
    fontWeight: "bold",
    color: theme.palette.text.secondary,
    borderRadius: 4,
    borderWidth: 2,
    borderStyle: "solid",
    borderColor: theme.palette.text.secondary,
    marginRight: theme.spacing(3),
    width: "30%",
    height: theme.spacing(7),
  },

  inviteButton: {
    flexGrow: 1,
    height: theme.spacing(7),
  },
}));

export interface InviteCoachDialogProps extends DialogProps {
  sending: boolean;
  onSend: (email: string, message: string) => void;
}

const getDefaultInviteMessage = (brandName: string) =>
  `Hello! \n\nI’d like to invite you to my team on ${brandName}. It’s a coaching platform that will help us stay organized and chat with each other.\n\nIt takes less than a minute to join – see you there!`;

export default function InviteCoachDialog(props: InviteCoachDialogProps) {
  const { className, onClose, sending, onSend, ...other } = props;
  const s = useStyles();
  const { brandName } = useCurrentBrand();

  const [email, setEmail] = React.useState<string>("");
  const [message, setMessage] = React.useState<string>(
    getDefaultInviteMessage(brandName),
  );

  React.useEffect(() => {
    if (!other.open) {
      setEmail("");
      setMessage(getDefaultInviteMessage(brandName));
    }
  }, [other.open, brandName]);

  const handleCancel = React.useCallback(() => {
    onClose?.(undefined, undefined);
  }, [onClose]);

  const disabled = sending || email.trim().length === 0;

  return (
    <BaseDialog
      className={clsx(s.root, className)}
      title={"Invite a coach"}
      classes={{
        paper: s.paper,
      }}
      onClose={onClose}
      {...other}
    >
      <Box>
        <TextField
          className={s.field}
          fullWidth
          label="Coach email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="coach@example.com"
        />

        <TextField
          fullWidth
          multiline
          label="Message"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          slotProps={{
            input: {
              disableUnderline: true,
              className: s.messageInput,
            },
          }}
        />

        <Box className={s.buttons}>
          <Button
            className={s.cancelButton}
            variant="outlined"
            children="Cancel"
            onClick={handleCancel}
            disabled={sending}
          />
          <LoadingActionButton
            className={s.inviteButton}
            children="Send Invite"
            onClick={() => onSend(email, message)}
            disabled={disabled}
            loading={sending}
          />
        </Box>
      </Box>
    </BaseDialog>
  );
}
